import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ListPlus, Plus, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Spinner } from '../common/States';
import { useShortlists } from '../../hooks/queries';
import { api, errorMessage } from '../../lib/api';
import { purposeLabel } from '../../lib/taxonomy';
import { cn } from '../../lib/utils';
import { invalidateShortlistData } from '../../lib/invalidate';

/**
 * "+ shortlist" popover: pick an open shortlist or create one, then add
 * one or several items (odoo ids). Renders `children` as the trigger.
 */
export default function ShortlistPicker({ ids, children, align = 'end', onAdded, label }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [done, setDone] = useState(null); // {id, name}
  const qc = useQueryClient();
  const { data, isLoading, error } = useShortlists(open);
  const list = useMemo(
    () =>
      (data || [])
        .filter((s) => s.status !== 'closed')
        .filter((s) => !filter || `${s.name} ${s.client || ''}`.toLowerCase().includes(filter.toLowerCase())),
    [data, filter]
  );
  const idList = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);

  const add = useMutation({
    mutationFn: async (shortlist) => {
      for (const id of idList) {
        await api.addToShortlist(shortlist.id, { odoo_product_id: id });
      }
      return shortlist;
    },
    onSuccess: (s) => {
      invalidateShortlistData(qc, s.id);
      setDone({ id: s.id, name: s.name });
      onAdded?.(s);
    },
  });

  const create = useMutation({
    mutationFn: (name) => api.createShortlist({ name, purpose: 'b2b_project', client: '', notes: '' }),
    onSuccess: (s) => {
      setNewName('');
      add.mutate(s);
    },
  });

  const busy = add.isPending || create.isPending;
  const err = add.error || create.error;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setDone(null);
          setFilter('');
          add.reset();
          create.reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        {children || (
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <ListPlus className="h-3.5 w-3.5" /> {label || 'Add to shortlist'}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-0" onClick={(e) => e.stopPropagation()}>
        <div className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          Add {idList.length > 1 ? `${idList.length} items` : 'item'} to a shortlist
        </div>
        {done ? (
          <div className="space-y-2 p-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <Check className="h-4 w-4" /> Added to <span className="font-medium">{done.name}</span>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline" className="h-8">
                <Link to={`/shortlists/${done.id}`}>Open shortlist</Link>
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => setDone(null)}>
                Add to another
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find shortlist…" className="h-8 pl-7 text-sm" />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto px-1 pb-1">
              {isLoading && (
                <div className="space-y-1 p-1">
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                </div>
              )}
              {error && <p className="px-2 py-2 text-xs text-destructive">{errorMessage(error)}</p>}
              {!isLoading && !error && !list.length && (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">{filter ? 'No match.' : 'No open shortlists yet.'}</p>
              )}
              {list.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  disabled={busy}
                  onClick={() => add.mutate(s)}
                  className={cn('flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50')}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{s.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {purposeLabel(s.purpose)}
                      {s.client ? ` · ${s.client}` : ''} · {s.item_count} items
                    </span>
                  </span>
                  {add.isPending && add.variables?.id === s.id ? <Spinner className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              ))}
            </div>
            <form
              className="flex gap-1.5 border-t p-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) create.mutate(newName.trim());
              }}
            >
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New shortlist name" className="h-8 text-sm" />
              <Button type="submit" size="sm" className="h-8 shrink-0" disabled={!newName.trim() || busy}>
                {create.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Create'}
              </Button>
            </form>
            {err && <p className="px-3 pb-2 text-xs text-destructive">{errorMessage(err)}</p>}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
